/**
 * The five standard non-happy states (SDD §7.4 inherited baseline, §9.3): every screen renders these from here, never ad hoc.
 * Grammar copied from the reference's own empty states (e.g. Trades `trNoRows` line 3268, AI Signals `sgNone` line 1379):
 * centred ghost icon, 12.5–13px `var(--text-mid)` line, 11–11.5px `var(--faint)` guidance line.
 */
import type { ReactNode } from 'react';
import { css } from './style';
import { CARD_BASE } from './surfaces';

export interface EmptyStateProps {
  /** Phosphor icon class, e.g. `ph-list-magnifying-glass`. */
  icon?: string;
  title: ReactNode;
  /** Guidance copy — what the trader can do next. Required by the baseline ("empty with guidance copy"). */
  guidance: ReactNode;
  /** Render inside a card surface (the reference does both: bare inside a card, or as its own card). */
  card?: boolean;
}
export function EmptyState({
  icon = 'ph-list-magnifying-glass',
  title,
  guidance,
  card = false,
}: EmptyStateProps) {
  const inner = (
    <>
      <i className={`ph ${icon}`} style={css('font-size:26px;color:var(--ghost)')}></i>
      <div style={css('font-size:12.5px;color:var(--text-mid);margin-top:9px')}>{title}</div>
      <div style={css('font-size:11px;color:var(--faint);margin-top:3px')}>{guidance}</div>
    </>
  );
  return card ? (
    <div style={css(`${CARD_BASE};padding:38px 20px;text-align:center`)}>{inner}</div>
  ) : (
    <div style={css('padding:34px;text-align:center')}>{inner}</div>
  );
}

/** Loading skeleton — shimmer-free (the reference has no shimmer): token-tinted blocks at the content's own rhythm. */
export interface LoadingSkeletonProps {
  rows?: number;
  height?: number;
  gap?: number;
  label?: string;
}
export function LoadingSkeleton({ rows = 3, height = 14, gap = 9, label = 'Loading' }: LoadingSkeletonProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      style={css(`display:flex;flex-direction:column;gap:${gap}px;padding:4px 0`)}
    >
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={css(`height:${height}px;border-radius:6px;background:var(--l6);width:${100 - i * 12}%`)}
        ></div>
      ))}
    </div>
  );
}

/** Degraded-source banner with the last-good timestamp (baseline: staleness > 15s or breaker open; data stays visible). */
export interface DegradedBannerProps {
  source: string;
  lastGood: string;
  onRetry?: () => void;
}
export function DegradedBanner({ source, lastGood, onRetry }: DegradedBannerProps) {
  return (
    <div
      role="status"
      style={css(
        'display:flex;align-items:center;gap:9px;padding:9px 13px;border-radius:11px;border:1px solid color-mix(in srgb, var(--amb) 40%, transparent);background:color-mix(in srgb, var(--amb) 10%, transparent)',
      )}
    >
      <i className="ph ph-warning" style={css('font-size:14px;color:var(--amb-2);flex:none')}></i>
      <span style={css('font-size:11.5px;color:var(--text-mid);flex:1')}>
        {source} is degraded — showing the last good data from {lastGood}.
      </span>
      {onRetry ? (
        <button
          onClick={onRetry}
          style={css(
            'font-size:11px;padding:4px 11px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
          )}
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}

/** Permission-denied state naming the unlocking plan or role (baseline: denial always names the tier). */
export interface PermissionDeniedProps {
  feature: string;
  requires: string;
  onUpgrade?: () => void;
}
export function PermissionDenied({ feature, requires, onUpgrade }: PermissionDeniedProps) {
  return (
    <div style={css(`${CARD_BASE};padding:38px 20px;text-align:center`)}>
      <i className="ph ph-lock-key" style={css('font-size:26px;color:var(--ghost)')}></i>
      <div style={css('font-size:12.5px;color:var(--text-mid);margin-top:9px')}>
        {feature} is part of {requires}
      </div>
      <div style={css('font-size:11px;color:var(--faint);margin-top:3px')}>
        Your current plan does not include it.
      </div>
      {onUpgrade ? (
        <button
          onClick={onUpgrade}
          style={css(
            'margin-top:12px;font-size:11.5px;padding:7px 15px;border-radius:99px;border:1px solid color-mix(in srgb, var(--acc-fill) 45%, transparent);background:transparent;color:var(--acc);cursor:pointer',
          )}
        >
          See {requires}
        </button>
      ) : null}
    </div>
  );
}

/** Failed state with a scoped retry boundary (baseline: the failure never takes the screen down). */
export interface FailedStateProps {
  what: string;
  detail?: string;
  onRetry?: () => void;
}
export function FailedState({ what, detail, onRetry }: FailedStateProps) {
  return (
    <div role="alert" style={css('padding:34px;text-align:center')}>
      <i className="ph ph-warning-octagon" style={css('font-size:26px;color:var(--neg)')}></i>
      <div style={css('font-size:12.5px;color:var(--text-mid);margin-top:9px')}>
        {what} could not be loaded
      </div>
      {detail === undefined ? null : (
        <div style={css('font-size:11px;color:var(--faint);margin-top:3px')}>{detail}</div>
      )}
      {onRetry ? (
        <button
          onClick={onRetry}
          style={css(
            'margin-top:12px;font-size:11.5px;padding:7px 15px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
          )}
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}
